const COLLECTION_GRID = document.querySelector('.mm-collection-grid');

/**
 * État de chargement de la grille — affiché SEULEMENT si la requête traîne.
 *
 * La plupart des filtrages reviennent en moins de 200ms : assombrir la grille dès le
 * clic produisait un aller-retour d'opacité si bref qu'il se lisait comme un
 * clignotement (« la page se vide puis revient »), alors même que tout allait vite.
 * En dessous du seuil, on ne montre donc rien du tout et le remplacement paraît
 * instantané ; au-dessus, l'état s'installe et devient une vraie information.
 *
 * Le début/fin est centralisé ici parce que refreshCollectionPageWithFilters est le
 * passage obligé de TOUS les chemins : mmCollectionFilterChanged, mais aussi les
 * `onchange="fakeSubmitForm(this)"` posés en Liquid, qui n'affichaient aucun état.
 */
const MM_GRID_LOADING_DELAY = 200;
let mmGridLoadingTimer = null;

function mmBeginGridLoading() {
  if (!COLLECTION_GRID) return;
  clearTimeout(mmGridLoadingTimer);
  mmGridLoadingTimer = setTimeout(function () {
    COLLECTION_GRID.classList.add('mm-loading');
  }, MM_GRID_LOADING_DELAY);
}

function mmEndGridLoading() {
  if (!COLLECTION_GRID) return;
  clearTimeout(mmGridLoadingTimer);
  mmGridLoadingTimer = null;
  COLLECTION_GRID.classList.remove('mm-loading');
}

/**
 * Précharge les premières images de la grille AVANT de l'échanger.
 *
 * C'est la vraie cause du « la page se vide puis se remplit » : filtrer change les
 * produits, donc les 50 cartes injectées portent 50 URLs d'images neuves, en
 * `loading="lazy"`. À l'instant de l'échange, aucune n'est encore chargée — on voit la
 * mise en page des cartes sans aucun visuel, le temps que le réseau réponde.
 * En attendant ici, l'ANCIENNE grille reste affichée (avec ses images) jusqu'à ce que la
 * nouvelle soit présentable : l'échange devient net, sans passage à vide.
 *
 * On ne précharge que le haut de grille (le reste est lazy et arrivera au scroll), et un
 * délai maximum garantit qu'une image lente ne retient jamais le filtrage en otage.
 *
 * `new Image()` et non les <img> du fragment : ceux-ci sont dans un arbre détaché, où
 * `loading="lazy"` empêche justement le chargement.
 */
const MM_GRID_PRELOAD_COUNT = 8;
const MM_GRID_PRELOAD_TIMEOUT = 700;

function mmPreloadGridImages(sectionEl) {
    const imgs = Array.prototype.slice.call(
        sectionEl.querySelectorAll('.mm-dc-product-image'), 0, MM_GRID_PRELOAD_COUNT
    );
    if (!imgs.length) return Promise.resolve();

    const loads = imgs.map(function (img) {
        return new Promise(function (resolve) {
            const src = img.getAttribute('src');
            if (!src) return resolve();
            const pre = new Image();
            // srcset/sizes recopiés : sans eux le navigateur préchargerait une autre
            // variante que celle qu'il choisira à l'affichage — cache manqué, donc
            // attente pour rien.
            const srcset = img.getAttribute('srcset');
            const sizes = img.getAttribute('sizes');
            if (sizes) pre.sizes = sizes;
            if (srcset) pre.srcset = srcset;
            pre.onload = pre.onerror = resolve;
            pre.src = src;
        });
    });

    return Promise.race([
        Promise.all(loads),
        new Promise(function (resolve) { setTimeout(resolve, MM_GRID_PRELOAD_TIMEOUT); })
    ]);
}

Shopify.queryParams = {};
/**
 * Refresh the collection page with the provided filters.
 *
 * @param {string} url_filters - The URL parameters for the filters.
 */
function refreshCollectionPageWithFilters(url_filters, submitMobile = false) {
    let currentUrl = "//" + location.host + location.pathname;
    mmBeginGridLoading();
    return httpGet(currentUrl + "?" + url_filters).then(newPageString => {
        let newPageEl = document.createElement("DIV");
        newPageEl.innerHTML = newPageString;

        let newSection = newPageEl.querySelector(".mm-collection-grid");
        return mmPreloadGridImages(newSection).then(() => {
            document.querySelector(".mm-collection-grid").innerHTML = newSection.innerHTML;

            if (window.innerWidth <= 1024) {
                updateMobileFilters(newPageEl);
            }

            updateProductsCount(newPageEl);
            updatePagination(newPageEl);
            updateDesktopFilterGroups(newPageEl);
            mmSyncDiscountPills();
            mmSyncYukaPills();
            mmSyncPreferencePills();
            mmUpdateFilterBadges();
            mmSetupFilterCollapse();
            // APRÈS mmSetupFilterCollapse, qui remet le repli « Voir plus » à zéro et
            // écraserait la coordination faite par la recherche.
            mmReapplyFilterSearch();

            window.history.replaceState(null, document.title, currentUrl + "?" + url_filters);
            mmEndGridLoading();
            startInitializingQuickAddElements();
        });
    }).catch(error => {
        // Sans ce catch, un échec réseau laissait la grille figée à jamais : l'état de
        // chargement n'était retiré que dans le chemin de succès, et il porte
        // `pointer-events: none` — plus une seule carte cliquable jusqu'au rechargement.
        mmEndGridLoading();
        console.error('Filtrage de la collection impossible :', error);
    });
}

/**
 * Aligne les facettes affichées (`dstRoot`) sur celles de la page renvoyée par un filtrage
 * AJAX (`srcRoot`), en les appariant par `data-filter-param` et NON par leur rang.
 *
 * L'appariement par index décalait toute la liste : Search & Discovery ne renvoie pas les
 * facettes qu'un filtre a vidées (choisir une marque sans produit noté Yuka fait disparaître
 * Yuka). Avec une facette de moins dans la réponse, chacune recevait le libellé / contenu de
 * sa voisine — en desktop, le dropdown Préférences s'affichait « Marque » et ouvrait les pictos.
 * D'où ici : facettes disparues retirées, réapparues réinsérées, ordre de la réponse respecté.
 *
 * Les facettes conservées sont DÉPLACÉES et mises à jour par `updateGroup`, pas recréées :
 * elles gardent leur état ouvert et leurs écouteurs. Une facette réapparue reprend le nœud de
 * la réponse tel quel ; `onInsert` lui branche ce qui n'est pas délégué.
 */
function mmReconcileFilterGroups(srcRoot, dstRoot, groupSelector, updateGroup, onInsert) {
    if (!srcRoot || !dstRoot) return;

    const groupsOf = root => Array.from(root.querySelectorAll(":scope > " + groupSelector));
    const srcGroups = groupsOf(srcRoot);
    const srcKeys = srcGroups.map(group => group.dataset.filterParam);

    groupsOf(dstRoot).forEach(group => {
        if (!srcKeys.includes(group.dataset.filterParam)) group.remove();
    });

    srcGroups.forEach((srcGroup, index) => {
        let dstGroup = groupsOf(dstRoot).find(group => group.dataset.filterParam === srcGroup.dataset.filterParam);
        if (dstGroup) {
            updateGroup(srcGroup, dstGroup);
        } else {
            dstGroup = srcGroup;
            if (onInsert) onInsert(dstGroup);
        }

        // Les rangs < index sont déjà en place : on ne déplace que si besoin, pour ne pas
        // faire perdre le focus (ex. champ de recherche marque) à une facette bien placée.
        const current = groupsOf(dstRoot);
        if (current[index] === dstGroup) return;
        if (current[index]) {
            current[index].before(dstGroup);
        } else if (current.length) {
            current[current.length - 1].after(dstGroup);
        } else {
            dstRoot.appendChild(dstGroup);
        }
    });
}

/**
 * Barre desktop : dropdowns `<details>` (mm-filter-dropdown-desktop). Les écouteurs sont
 * délégués sur la barre (mmBindFilterInteractions) : un dropdown réinséré est actif d'emblée.
 */
function updateDesktopFilterGroups(newPageEl) {
    mmReconcileFilterGroups(
        newPageEl.querySelector(".mm-collection-filters-container"),
        document.querySelector(".mm-collection-filters-container"),
        ".filter-group",
        updateDesktopFilterGroup
    );
}

function updateDesktopFilterGroup(srcGroup, dstGroup) {
    dstGroup.id = srcGroup.id; // `filter-group-N` est positionnel : il suit le nouveau rang

    // Pastille : remplacée EN ENTIER plutôt que de bricoler le badge. Une version précédente
    // recréait celui-ci à la main en oubliant la classe `mm-filter-count`, qui porte le style
    // du compteur et l'état actif de la pastille via `:has(.mm-filter-count)`, et laissait un
    // badge vide à la désactivation. La pastille n'a que du contenu statique et aucun
    // écouteur : la remplacer en bloc est sans risque.
    const srcPill = srcGroup.querySelector(".filter-pill");
    const dstPill = dstGroup.querySelector(".filter-pill");
    if (srcPill && dstPill) dstPill.innerHTML = srcPill.innerHTML;

    // Contenu : on remplace les listes natives et les rangées de pills (seuils de réduction,
    // Yuka, Préférences), mais pas le reste du dropdown — le champ de recherche marque garde
    // ainsi sa saisie. L'état actif des pills est resynchronisé ensuite par
    // mmSyncDiscountPills / mmSyncYukaPills / mmSyncPreferencePills depuis les cases natives.
    const PARTS = ".filter-group-display__list, .mm-filter-rows, .mm-pref-pills";
    const srcParts = srcGroup.querySelectorAll(PARTS);
    const dstParts = dstGroup.querySelectorAll(PARTS);
    if (srcParts.length === dstParts.length) {
        srcParts.forEach((part, index) => {
            dstParts[index].innerHTML = part.innerHTML;
        });
        return;
    }

    // Structure changée DANS le dropdown (ex. le bandeau Préférences « les + recherchées »,
    // rendu seulement s'il lui reste des valeurs) : un appariement par rang mélangerait les
    // rangées, on reprend donc tout le contenu.
    const srcDisplay = srcGroup.querySelector(".filter-group-display");
    const dstDisplay = dstGroup.querySelector(".filter-group-display");
    if (srcDisplay && dstDisplay) dstDisplay.innerHTML = srcDisplay.innerHTML;
}

/**
 * Drawer mobile : segments accordéon (mm-filter-segment). Même appariement par facette que
 * la barre desktop. L'accordéon, lui, est branché élément par élément au chargement : un
 * segment réinséré doit donc être rebranché (mmToggleMobileDropdown).
 */
function updateMobileFilters(newPageEl) {
    mmReconcileFilterGroups(
        newPageEl.querySelector(".mm-mobile-facets-collection .mm-mfc-dropdown-filters"),
        document.querySelector(".mm-mobile-facets-collection .mm-mfc-dropdown-filters"),
        ".mm-mfc-dropdown-container",
        (srcGroup, dstGroup) => {
            const srcText = srcGroup.querySelector(".mm-mfc-dropdown-text");
            const dstText = dstGroup.querySelector(".mm-mfc-dropdown-text");
            if (srcText && dstText) dstText.innerHTML = srcText.innerHTML;
        },
        group => {
            const collapsible = group.querySelector(".mm-mfc-dropdown-collapsible");
            if (collapsible) collapsible.addEventListener("click", mmToggleMobileDropdown);
        }
    );
    let newBtnMobileCount = newPageEl.querySelector("#applyFiltersButton");
    document.querySelector("#applyFiltersButton").innerHTML = newBtnMobileCount.innerHTML;
}

function updateProductsCount(newPageEl) {
    let newProductsCount = newPageEl.querySelector(".mm-header-collection-products-count");
    if (!newProductsCount) return;
    // Met à jour TOUTES les instances (header desktop + compteur mobile au-dessus
    // de la grille) après un changement de filtre.
    document.querySelectorAll(".mm-header-collection-products-count").forEach(function (el) {
        el.innerHTML = newProductsCount.innerHTML;
    });
}

function updatePagination(newPageEl) {
    let newPagination = newPageEl.querySelector(".pagination-wrapper");
    if (newPagination) {
        document.querySelector(".pagination-wrapper").innerHTML = newPagination.innerHTML;
    } else {
        document.querySelector(".pagination-wrapper").innerHTML = '';
    }
}

/**
 * Compte les filtres actifs (100% client-side, aucune requête) et affiche un
 * badge numérique sur chaque segment du bottom sheet et sur la chip
 * « Filtres & Tri ». Le tri est volontairement exclu : c'est un choix unique
 * toujours renseigné, pas un filtre.
 */
function mmUpdateFilterBadges() {
  const drawer = document.querySelector('.mm-mobile-facets-collection');
  if (!drawer) return;

  let total = 0;

  drawer.querySelectorAll('.mm-mfc-dropdown-container').forEach(function (container) {
    const title = container.querySelector('.mm-mfc-dropdown-title');
    const content = container.querySelector('.mm-mfc-dropdown-text');
    if (!title || !content) return;

    let count;
    const singleSelectPills = content.querySelectorAll('[data-mm-discount-threshold], [data-mm-yuka-threshold]');
    if (singleSelectPills.length) {
      // Segments à seuil (% de remise, Yuka) : une seule pill active = 1 filtre (pas N cases).
      count = content.querySelectorAll('[data-mm-discount-threshold].is-active, [data-mm-yuka-threshold].is-active').length;
    } else {
      count = content.querySelectorAll('input[type="checkbox"]:checked').length;
      let priceActive = false;
      content.querySelectorAll('.filter-group-display__price-range input[type="number"]').forEach(function (input) {
        if (input.value.trim() !== '') priceActive = true;
      });
      if (priceActive) count += 1;
    }

    total += count;

    let badge = title.querySelector('.mm-mfc-badge');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'mm-mfc-badge';
        title.appendChild(badge);
      }
      badge.textContent = count;
      badge.setAttribute('aria-label', count + ' filtre(s) actif(s)');
    } else if (badge) {
      badge.remove();
    }
  });

  const chip = document.querySelector('.mm-collection-chip-filters');
  if (chip) {
    let chipBadge = chip.querySelector('.mm-filter-badge');
    if (total > 0) {
      if (!chipBadge) {
        chipBadge = document.createElement('span');
        chipBadge.className = 'mm-filter-badge';
        chip.appendChild(chipBadge);
      }
      chipBadge.textContent = total;
    } else if (chipBadge) {
      chipBadge.remove();
    }

    if (!chip.dataset.baseLabel) {
      chip.dataset.baseLabel = chip.getAttribute('aria-label') || '';
    }
    chip.setAttribute(
      'aria-label',
      total > 0 ? chip.dataset.baseLabel + ' — ' + total + ' actif(s)' : chip.dataset.baseLabel
    );
  }
}

/**
 * Filtre en direct (client-side) la liste d'un segment (ex. Marques) selon le
 * texte saisi dans son champ de recherche. Aucune requête serveur.
 */
function mmFilterListBySearch(input) {
  const content = input.closest('.mm-mfc-dropdown-text');
  const list = content && content.querySelector('.filter-group-display__list');
  if (!list) return;

  const query = input.value.replace(/\s+/g, ' ').trim().toLowerCase();
  list.querySelectorAll('.filter-group-display__list-item').forEach(function (item) {
    const label = item.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
    item.style.display = label.indexOf(query) === -1 ? 'none' : '';
  });

  // Coordination avec « Voir plus » : pendant une recherche on ignore le repli
  // (tous les résultats visibles) ; à l'effacement on rétablit l'état de repli.
  const toggle = content.querySelector('.mm-filter-more');
  if (toggle) {
    const collList = toggle.previousElementSibling;
    if (query) {
      collList.classList.remove('mm-collapsed');
      toggle.style.display = 'none';
    } else {
      collList.classList.toggle('mm-collapsed', collList._mmDesiredCollapsed !== false);
      toggle.style.display = '';
    }
  }

  // La liste a changé de hauteur (résultats filtrés / repli) → réaligne l'accordéon.
  mmRefreshDropdownHeight(content);
}

/**
 * Réapplique les recherches en cours après un rafraîchissement AJAX.
 * updateInnerLists remplace le <ul> d'un segment sans toucher à son champ de
 * recherche : sans ça, cocher une marque pendant une recherche rendait la liste
 * complète alors que le champ affiche toujours le texte saisi.
 */
function mmReapplyFilterSearch() {
  document.querySelectorAll('[data-mm-filter-search]').forEach(function (input) {
    if (input.value.trim()) mmFilterListBySearch(input);
  });
}

/**
 * « Voir plus / Voir moins » par segment : au-delà de MM_FILTER_ROW_LIMIT lignes,
 * on replie la liste et on injecte un toggle. Ne se déclenche que si ça cache au
 * moins 2 lignes. Les options sélectionnées ne sont jamais masquées : si un
 * filtre actif est au-delà du seuil, le segment démarre déplié. Idempotent (ré-
 * exécutable après remplacement AJAX du contenu du drawer).
 */
const MM_FILTER_ROW_LIMIT = 5;

/**
 * L'accordéon de section fige sa hauteur (maxHeight = scrollHeight) à l'ouverture.
 * Dès qu'on change le nombre de lignes visibles (Voir plus/moins, recherche), il
 * faut réaligner cette hauteur sinon overflow:hidden coupe les lignes révélées.
 * No-op si la section est fermée (maxHeight vide/0).
 */
function mmRefreshDropdownHeight(el) {
  const box = el && el.closest ? el.closest('.mm-mfc-dropdown-text') : null;
  if (box && box.style.maxHeight && box.style.maxHeight !== '0px') {
    box.style.maxHeight = box.scrollHeight + 'px';
  }
}

function mmSetupOneCollapse(list) {
  // Un toggle déjà posé peut être PÉRIMÉ : après un filtrage AJAX, updateInnerLists
  // remplace le contenu de la liste mais pas le toggle, dont le libellé garde le compte
  // d'avant (« Voir plus (12) » alors que 3 lignes sont cachées) — il est figé dans la
  // closure de `hidden`. On le retire et on le reconstruit sur la liste réelle.
  // Le drawer mobile ne connaissait pas le problème : il remplace tout le segment.
  const existing = list.nextElementSibling;
  if (existing && existing.classList.contains('mm-filter-more')) existing.remove();

  const rows = Array.prototype.filter.call(list.children, function (el) { return el.nodeType === 1; });
  if (rows.length <= MM_FILTER_ROW_LIMIT + 1) {
    // Plus assez de lignes pour mériter un repli. Il FAUT retirer `mm-collapsed` :
    // à 6 lignes pile, la 6ᵉ resterait masquée par le CSS sans aucun toggle pour
    // la révéler.
    list.classList.remove('mm-collapsed');
    return;
  }

  const hidden = rows.length - MM_FILTER_ROW_LIMIT;
  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'mm-filter-more';

  function apply(collapsed) {
    list.classList.toggle('mm-collapsed', collapsed);
    list._mmDesiredCollapsed = collapsed;
    const sign = collapsed ? '+' : '−'; // − (vrai signe moins)
    const label = collapsed ? 'Voir plus (' + hidden + ')' : 'Voir moins';
    toggle.innerHTML = '<span class="mm-filter-more-sign" aria-hidden="true">' + sign +
      '</span><span class="mm-filter-more-text">' + label + '</span>';
    toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    mmRefreshDropdownHeight(list); // réaligne la hauteur de l'accordéon
  }

  // Un filtre actif au-delà du seuil ? → on démarre déplié (jamais caché).
  const activeHidden = rows.slice(MM_FILTER_ROW_LIMIT).some(function (r) {
    return r.classList.contains('is-active') || r.querySelector('input:checked');
  });

  // À la reconstruction, on repart du choix de l'utilisateur (`_mmDesiredCollapsed`,
  // porté par la liste et non par le toggle) : sans ça, une liste qu'il venait de
  // déplier se refermait sous ses yeux au premier filtrage. Un filtre actif caché
  // force malgré tout l'ouverture.
  const wanted = typeof list._mmDesiredCollapsed === 'boolean' ? list._mmDesiredCollapsed : true;

  list.after(toggle);
  apply(wanted && !activeHidden);
  toggle.addEventListener('click', function () {
    apply(!list.classList.contains('mm-collapsed'));
  });
}

function mmSetupFilterCollapse() {
  document.querySelectorAll('.mm-mobile-facets-collection .mm-mfc-dropdown-text, .mm-collection-filters-container .mm-mfc-dropdown-text').forEach(function (box) {
    // Listes custom (Réduction/Yuka = .mm-filter-rows ; Préférences « reste » =
    // .mm-pref-pills enfant direct, hors bandeau « + recherchées »).
    box.querySelectorAll(':scope > .mm-filter-rows, :scope > .mm-pref-pills').forEach(mmSetupOneCollapse);
    // Listes natives visibles (Marque/Type/Sous-rayon) — on saute les listes
    // sources masquées des segments custom.
    box.querySelectorAll('.filter-group-display__list').forEach(function (list) {
      if (list.classList.contains('mm-checkboxes-discounts') ||
          list.classList.contains('mm-checkboxes-yuka') ||
          list.classList.contains('mm-checkboxes-preferences')) return;
      mmSetupOneCollapse(list);
    });
  });
}

/**
 * Filtre réduction — pills « X% et plus » à choix unique, mappées 1:1 sur les
 * valeurs (40/45/50). Le cumulatif vit dans la donnée (liste
 * mm_discount_thresholds : un produit à 47% porte [40,45]), donc cocher la case
 * « 40 » suffit à renvoyer tous les produits >= 40%. Une seule pill active ;
 * re-tap → tout se décoche. Aucune requête : le filtre s'applique au tap du CTA.
 */
function mmSelectDiscountThreshold(pill) {
  const group = pill.closest('.mm-mfc-dropdown-text');
  if (!group) return;

  const wasActive = pill.classList.contains('is-active');
  const value = pill.getAttribute('data-mm-discount-threshold');

  group.querySelectorAll('[data-mm-discount-threshold]').forEach(function (p) {
    p.classList.remove('is-active');
    p.setAttribute('aria-pressed', 'false');
  });

  const checkboxes = group.querySelectorAll('.mm-checkboxes-discounts input[type="checkbox"]');
  checkboxes.forEach(function (cb) { cb.checked = false; });

  if (!wasActive) {
    pill.classList.add('is-active');
    pill.setAttribute('aria-pressed', 'true');
    checkboxes.forEach(function (cb) {
      if (cb.value === value) cb.checked = true;
    });
  }

  mmUpdateFilterBadges();
}

/**
 * Aligne l'état visuel des pills sur la case cochée (état URL / après AJAX).
 */
function mmSyncDiscountPills() {
  document.querySelectorAll('.mm-mobile-facets-collection .mm-mfc-dropdown-text, .mm-collection-filters-container .mm-mfc-dropdown-text').forEach(function (group) {
    const pills = group.querySelectorAll('[data-mm-discount-threshold]');
    if (!pills.length) return;

    const checked = group.querySelector('.mm-checkboxes-discounts input[type="checkbox"]:checked');
    const activeValue = checked ? checked.value : null;

    pills.forEach(function (pill) {
      const active = activeValue !== null && pill.getAttribute('data-mm-discount-threshold') === activeValue;
      pill.classList.toggle('is-active', active);
      pill.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  });
}

/**
 * Filtre Yuka — pills « seuil » à choix unique, mappées 1:1 sur les valeurs
 * (25/50/75). Même mécanique que la réduction : le cumulatif vit dans la donnée
 * (liste mm_yuka_thresholds : un produit à 80 porte [25,50,75]), donc cocher la
 * case « 50 » suffit à renvoyer tous les produits >= 50. Une seule pill active ;
 * re-tap → tout se décoche. Aucune requête : appliqué au tap du CTA.
 */
function mmSelectYukaThreshold(pill) {
  const group = pill.closest('.mm-mfc-dropdown-text');
  if (!group) return;

  const wasActive = pill.classList.contains('is-active');
  const value = pill.getAttribute('data-mm-yuka-threshold');

  group.querySelectorAll('[data-mm-yuka-threshold]').forEach(function (p) {
    p.classList.remove('is-active');
    p.setAttribute('aria-pressed', 'false');
  });

  const checkboxes = group.querySelectorAll('.mm-checkboxes-yuka input[type="checkbox"]');
  checkboxes.forEach(function (cb) { cb.checked = false; });

  if (!wasActive) {
    pill.classList.add('is-active');
    pill.setAttribute('aria-pressed', 'true');
    checkboxes.forEach(function (cb) {
      if (cb.value === value) cb.checked = true;
    });
  }

  mmUpdateFilterBadges();
}

/**
 * Aligne l'état visuel des pills Yuka sur la case cochée (état URL / après AJAX).
 */
function mmSyncYukaPills() {
  document.querySelectorAll('.mm-mobile-facets-collection .mm-mfc-dropdown-text, .mm-collection-filters-container .mm-mfc-dropdown-text').forEach(function (group) {
    const pills = group.querySelectorAll('[data-mm-yuka-threshold]');
    if (!pills.length) return;

    const checked = group.querySelector('.mm-checkboxes-yuka input[type="checkbox"]:checked');
    const activeValue = checked ? checked.value : null;

    pills.forEach(function (pill) {
      const active = activeValue !== null && pill.getAttribute('data-mm-yuka-threshold') === activeValue;
      pill.classList.toggle('is-active', active);
      pill.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  });
}

/**
 * Filtre Préférences — pills « icône + libellé » MULTI-select. Chaque pill
 * pilote sa case native (masquée) en 1:1 par égalité de valeur (GID). Plusieurs
 * pills peuvent être actives. Aucune requête : appliqué au tap du CTA.
 */
function mmTogglePreferencePill(pill) {
  const group = pill.closest('.mm-mfc-dropdown-text');
  if (!group) return;

  const value = pill.getAttribute('data-mm-pref-value');
  const nowActive = !pill.classList.contains('is-active');

  pill.classList.toggle('is-active', nowActive);
  pill.setAttribute('aria-pressed', nowActive ? 'true' : 'false');

  group.querySelectorAll('.mm-checkboxes-preferences input[type="checkbox"]').forEach(function (cb) {
    if (cb.value === value) cb.checked = nowActive;
  });

  mmUpdateFilterBadges();
}

/**
 * Aligne l'état visuel des pills Préférences sur les cases cochées
 * (état URL / après AJAX). Multi-select : chaque pill reflète sa case.
 */
function mmSyncPreferencePills() {
  document.querySelectorAll('.mm-mobile-facets-collection .mm-mfc-dropdown-text, .mm-collection-filters-container .mm-mfc-dropdown-text').forEach(function (group) {
    const pills = group.querySelectorAll('[data-mm-pref-value]');
    if (!pills.length) return;

    pills.forEach(function (pill) {
      const value = pill.getAttribute('data-mm-pref-value');
      let active = false;
      group.querySelectorAll('.mm-checkboxes-preferences input[type="checkbox"]:checked').forEach(function (cb) {
        if (cb.value === value) active = true;
      });
      pill.classList.toggle('is-active', active);
      pill.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  });
}

function httpGet(theUrl) {
    return new Promise((resolve, reject) => {
        let xmlHttp = new XMLHttpRequest();
        xmlHttp.open('GET', theUrl, true);
        xmlHttp.onload = function() {
            if (xmlHttp.status >= 200 && xmlHttp.status < 300) {
                resolve(xmlHttp.responseText);
            } else {
                reject(new Error('Request failed: ' + xmlHttp.statusText));
            }
        };
        xmlHttp.onerror = function() {
            reject(new Error('Network error'));
        };
        xmlHttp.send();
    });
}

function initializeQuickAddElements() {
    const quickAddElementsTesting = document.querySelectorAll('.mm-collection-grid .mm-product-card quick-add > button');
    const productCards = document.querySelectorAll('.mm-collection-grid .mm-product-card');

    if (quickAddElementsTesting.length == productCards.length) {
      const quickAddElements = document.querySelectorAll('quick-add');
        quickAddElements.forEach(el => {
            new QuickAdd(el);
        });
        return true;
    }
    return false;
}

function startInitializingQuickAddElements() {
    const intervalId = setInterval(() => {
        if (initializeQuickAddElements()) {
            clearInterval(intervalId);
        }
    }, 1000); // Relancer toutes les secondes
}


/**
 * Check for changes in the filter inputs and submit the form if changes are detected.
 */
let form = document.getElementById("collectionFacetsForm");

function checkForChanges() {
    var inputs = form.querySelectorAll('input');

    inputs.forEach(function (input) {
        input.addEventListener('change', function () {
            COLLECTION_GRID.classList.add('mm-loading');
            // mmInitSplide();
        });
    });
}

//checkForChanges();

function mmCollectionFilterChanged(clickedInput) {
  // L'état de chargement est posé par refreshCollectionPageWithFilters (après un délai) :
  // l'ajouter ici le rendait immédiat, et sautait les chemins passant directement par
  // fakeSubmitForm.
  setTimeout(() => {
    fakeSubmitForm(clickedInput);
  }, "0");
}

// Avec le tri du prix au kilo
// function mmCollectionFilterChanged(clickedInput) {
//     COLLECTION_GRID.classList.add('mm-loading');
    
//     const selectedValue = clickedInput.value;
  
//     // Vérifie si le tri personnalisé est sélectionné
//     if (selectedValue === 'custom-rating') {
//       const currentUrl = new URL(window.location.href);
//       currentUrl.searchParams.set('sort_by', 'custom-rating');
//       window.location.href = currentUrl.href;
//     } else {
//       setTimeout(() => {
//         fakeSubmitForm(clickedInput);
//       }, 0);
//     }
//   }

// Le listener `.sort_by` a été retiré avec le <select> de tri desktop, son unique
// cible. Il rechargeait la page sur `new URLSearchParams(Shopify.queryParams)` — un
// objet jamais alimenté depuis l'URL (il naît vide en tête de fichier) : changer le
// tri en desktop effaçait donc TOUS les filtres actifs. Le contrôle de tri partagé
// repart de l'URL courante et les préserve.

// Tri inline (« Trié par XXX ▾ » au-dessus de la grille, mobile) : menu déroulant
// ancré. Délégation globale (le contrôle survit aux refresh AJAX des filtres).
function mmCloseSortInline(inline) {
	if (!inline) return;
	inline.classList.remove('is-open');
	var trigger = inline.querySelector('[data-mm-sort-trigger]');
	if (trigger) trigger.setAttribute('aria-expanded', 'false');
}
document.addEventListener('click', function(e) {
	var opt = e.target.closest('[data-mm-sort-opt]');
	// Sélection d'un tri : on repart de l'URL courante pour PRÉSERVER les filtres
	// actifs (contrairement au listener .sort_by qui repart d'un queryParams vide).
	if (opt) {
		var params = new URLSearchParams(window.location.search);
		params.set('sort_by', opt.getAttribute('data-mm-sort-value'));
		params.delete('page');
		window.location.search = params.toString();
		return;
	}

	var openInline = document.querySelector('.mm-collection-sort-inline.is-open');
	var trigger = e.target.closest('[data-mm-sort-trigger]');
	if (trigger) {
		var inline = trigger.closest('.mm-collection-sort-inline');
		var willOpen = !inline.classList.contains('is-open');
		if (openInline && openInline !== inline) mmCloseSortInline(openInline);
		if (willOpen) {
			// Cale le bord gauche du menu sur le début de la valeur (« M » de
			// « Meilleures ventes »), pas sur « Trié par ». offsetLeft est mesuré
			// par rapport à .mm-collection-sort-inline (position:relative).
			var value = inline.querySelector('.mm-collection-sort-inline-value');
			var menu = inline.querySelector('.mm-collection-sort-inline-menu');
			if (value && menu) menu.style.left = value.offsetLeft + 'px';
		}
		inline.classList.toggle('is-open', willOpen);
		trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
		return;
	}

	// Clic en dehors → on ferme le menu ouvert.
	if (openInline) mmCloseSortInline(openInline);
});
document.addEventListener('keydown', function(e) {
	if (e.key === 'Escape') mmCloseSortInline(document.querySelector('.mm-collection-sort-inline.is-open'));
});

// Fermeture des pills
document.addEventListener('click', function(event) {
  if (window.innerWidth > 1024) {
      // Cible déjà détachée du DOM au moment où l'événement nous parvient : un
      // handler en amont a réécrit son conteneur pendant la remontée. C'est le cas
      // de « Voir plus », dont le clic atterrit sur un <span> intérieur que `apply()`
      // remplace aussitôt via innerHTML — `filter.contains()` répondait alors « non »
      // et refermait le dropdown qu'on venait juste de déplier.
      // Un nœud orphelin n'est ni dedans ni dehors : on ne peut rien en conclure.
      if (!event.target.isConnected) return;

      let openFilters = document.querySelectorAll('.filter-group[open]');

      openFilters.forEach(filter => {
          if (!filter.contains(event.target)) {
              filter.removeAttribute('open');
          }
      });
  }
});

/**
 * Serialize the form data and refresh the collection page with the filters.
 */
function fakeSubmitForm(clickedInput, submitMobile = false) {
  let form = clickedInput.closest('form.filter-form');
  let url_filters = new URLSearchParams(new FormData(form)).toString();
  refreshCollectionPageWithFilters(url_filters, submitMobile);
}

/**
 * Branche les interactions de filtres (recherche intra-segment, pills réduction / Yuka /
 * préférences) sur une racine donnée : le drawer mobile ou la barre de filtres desktop.
 * Délégation → survit aux remplacements AJAX du contenu des dropdowns.
 *
 * @param {Element} root            drawer mobile ou barre de filtres desktop
 * @param {boolean} applyImmediately relance la collection dès le clic.
 *   Les pills cochent les cases natives en JS, ce qui NE déclenche pas leur `onchange` :
 *   personne ne lance donc la requête. Le drawer mobile s'en accommode — il applique tout
 *   au tap de « Voir les produits » — mais la barre desktop n'a pas de CTA : sans ça, la
 *   pill s'activait sans que la grille ne bouge.
 */
function mmBindFilterInteractions(root, applyImmediately) {
  if (!root) return;

  root.addEventListener('input', function (event) {
    const searchInput = event.target.closest('[data-mm-filter-search]');
    if (searchInput) mmFilterListBySearch(searchInput);
  });

  root.addEventListener('click', function (event) {
    const discountPill = event.target.closest('[data-mm-discount-threshold]');
    if (discountPill) {
      mmSelectDiscountThreshold(discountPill);
      if (applyImmediately) mmCollectionFilterChanged(discountPill);
      return;
    }

    const yukaPill = event.target.closest('[data-mm-yuka-threshold]');
    if (yukaPill) {
      mmSelectYukaThreshold(yukaPill);
      if (applyImmediately) mmCollectionFilterChanged(yukaPill);
      return;
    }

    const prefPill = event.target.closest('[data-mm-pref-value]');
    if (prefPill) {
      mmTogglePreferencePill(prefPill);
      if (applyImmediately) mmCollectionFilterChanged(prefPill);
    }
  });

  // Cases natives d'un segment coché DIRECTEMENT par l'utilisateur (ex. Marque) :
  // le markup du drawer ne porte pas d'`onchange`, il applique tout au CTA. Sans CTA
  // côté desktop, on relance ici.
  // Scope `.mm-mfc-dropdown-text` = contenu venu de mm-filter-segment UNIQUEMENT. Les
  // listes rendues par le markup desktop d'origine (Sous-rayon, Type…) gardent leur
  // `onchange="fakeSubmitForm(this)"` inline et partiraient deux fois si on écoutait
  // toute la barre.
  // Les pills, elles, cochent leur case en JS : ça ne déclenche pas `change`, donc pas
  // de double requête avec les handlers de clic ci-dessus.
  if (applyImmediately) {
    root.addEventListener('change', function (event) {
      const box = event.target.closest('.mm-mfc-dropdown-text input[type="checkbox"]');
      if (box) mmCollectionFilterChanged(box);
    });
  }
}

// Badges de filtres actifs : recalcul instantané au chargement et à chaque
// changement dans le bottom sheet (délégation, survit aux remplacements AJAX).
document.addEventListener('DOMContentLoaded', function() {
  const drawer = document.querySelector('.mm-mobile-facets-collection');
  if (drawer) {
    // Badges du drawer : spécifiques au mobile, ils restent sur cette racine.
    drawer.addEventListener('change', mmUpdateFilterBadges);
    drawer.addEventListener('input', mmUpdateFilterBadges);
  }
  // Pills et recherche : elles vivent dans le drawer mobile ET, depuis la reprise du
  // style sur desktop, dans les dropdowns de la barre. On délègue donc sur les DEUX
  // racines — écoutées sur le seul drawer, les interactions desktop restaient sans effet.
  // Seule la barre desktop relance la collection au clic : le drawer, lui, attend son CTA.
  mmBindFilterInteractions(drawer, false);
  mmBindFilterInteractions(document.querySelector('.mm-collection-filters-container'), true);
  mmSyncDiscountPills();
  mmSyncYukaPills();
  mmSyncPreferencePills();
  mmUpdateFilterBadges();
  mmSetupFilterCollapse();
});

// Sort dropdown close handler
document.addEventListener('DOMContentLoaded', function() {
  const sortHeader = document.querySelector('.mm-sort-header');
  const sortList = document.querySelector('.mm-sort-list');

  if (sortHeader && sortList) {
    sortHeader.addEventListener('click', function(e) {
      e.stopPropagation();
    });

    sortList.addEventListener('click', function(e) {
      e.stopPropagation();
    });

    document.addEventListener('click', function(e) {
      if (sortList.classList.contains('mm-open') && !sortList.contains(e.target) && !sortHeader.contains(e.target)) {
        sortList.classList.remove('mm-open');
        sortHeader.classList.remove('mm-active');
      }
    });
  }
});

/**
 * Ouvre / ferme un segment accordéon du drawer mobile (écouteur posé sur son
 * `.mm-mfc-dropdown-collapsible`). Hors du bloc mobile ci-dessous : updateMobileFilters
 * s'en sert pour rebrancher un segment réinséré après un filtrage.
 */
function mmToggleMobileDropdown() {
  this.classList.toggle("active");

  const content = this.nextElementSibling;

  if (content.style.maxHeight) {
    content.style.maxHeight = null;
  } else {
    content.style.maxHeight = content.scrollHeight + "px";
  }
}

// Functions for handling mobile filter drawer
if (window.innerWidth < 1024) {
    const wrapperMobile = document.querySelector(".mm-mobile-facets-collection");
    const filtersContainer = document.querySelector(".filters-container");

    /**
     * Open the mobile filters drawer.
     */
    function mmOpenFiltersMobile() {
        wrapperMobile.classList.add('mm-open');
        filtersContainer.style.zIndex = "20";

        filtersContainer.style.zIndex = "20";

        document.querySelector('html').classList.add('stop-scrolling');
    }

    /**
     * Close the mobile filters drawer.
     */
    function mmCloseFiltersMobile() {
        wrapperMobile.classList.remove('mm-open');
        filtersContainer.style.removeProperty("z-index");

        document.querySelector('html').classList.remove('stop-scrolling');
    }

    /**
     * Check if the URL contains the 'open_filters' parameter and open the mobile filters drawer if it does.
     */
    function mmCheckDrawer() {
        var currentUrl = document.location.href.indexOf('?open_filters');
        if (currentUrl !== -1) {
            mmOpenFiltersMobile();
        }
    }


    /*Dropdonws filters */
    const facetsDropdown = document.querySelectorAll(".mm-mfc-dropdown-collapsible");
    let q;

    for (q = 0; q < facetsDropdown.length; q++) {
      facetsDropdown[q].addEventListener("click", mmToggleMobileDropdown);
    }

    /* Submit form mobile*/
    const drawerCtaMobile = document.getElementById("applyFiltersButton");
    drawerCtaMobile.addEventListener("click", function () {
      // Le bouton est dans #collectionFacetsFormMobile → closest('form.filter-form')
      // résout le form mobile. (Auparavant on passait `true`, d'où un TypeError
      // qui empêchait l'application des filtres mobiles.)
      fakeSubmitForm(drawerCtaMobile, true);
    });
}




// FILTRES DE POURCENTAGE



/*********************************************************************************************
 * Functions for handling price range sliders.
 */;
 const minRange = document.getElementById('min-percent-range');
 const maxRange = document.getElementById('max-percent-range');
 const sliderTrack = document.querySelector('.slider-track');
 const minRangeValue = document.querySelector('.price-min-value-change');
 const maxRangeValue = document.querySelector('.price-max-value-change');
 
 function updateRange() {
     const minValue = parseInt(minRange.value, 10);
     const maxValue = parseInt(maxRange.value, 10);
 
     // Met à jour les valeurs affichées
     minRangeValue.textContent = `${minValue}%`;
     maxRangeValue.textContent = `${maxValue}%`;
 
     // Calcule les pourcentages pour la piste
     const minPercent = (minValue / minRange.max) * 100;
     const maxPercent = (maxValue / maxRange.max) * 100;
 
     sliderTrack.style.background = `linear-gradient(to right, #ddd ${minPercent}%, var(--black-900) ${minPercent}%, var(--black-900) ${maxPercent}%, #ddd ${maxPercent}%)`;
 }
 
 minRange.addEventListener('input', function () {
     if (parseInt(minRange.value) > parseInt(maxRange.value) - 10) {
         minRange.value = maxRange.value - 10;
     }
     updateRange();
 });
 
 maxRange.addEventListener('input', function () {
     if (parseInt(maxRange.value) < parseInt(minRange.value) + 10) {
         maxRange.value = parseInt(minRange.value) + 10;
     }
     updateRange();
 });
 
 // Initialiser le slider
 updateRange();

/**
 * Update the sort display and close the dropdown after selection.
 */
function mmUpdateSortDisplay(input) {
  if (!input.checked) return;

  const sortHeader = document.querySelector('.mm-sort-header');
  const sortCurrent = sortHeader?.querySelector('.mm-sort-current');
  const sortList = document.querySelector('.mm-sort-list');

  if (sortHeader && sortCurrent && sortList) {
    const label = input.closest('label').querySelector('span').textContent;
    sortCurrent.textContent = label;
    sortList.classList.remove('mm-open');
    sortHeader.classList.remove('mm-active');
  }
}
